/**
 * Ports Layer
 * Data-driven layer for displaying port locations
 * Uses ensureGeoJsonLayer for deterministic, idempotent layer management.
 */

import { ENDPOINTS } from '@/feat/api-service-layer/endpoints';
import { BASE_URL } from '@/feat/api-service-layer/fetchClient';
import { removeGeoJsonLayer, waitForStyleLoaded } from '../geoJsonLayerHelper';
import { imagePreloader } from '../ImagePreloader';

const SOURCE_ID = 'Ports Source';
const LAYER_ID = 'Ports Layer';
const ICON_ID = 'portIcon';

export interface Port {
  longitude: number;
  latitude: number;
  name?: string;
  [key: string]: unknown;
}

/**
 * Fetch ports from API
 * @param getToken - Function to get authentication token
 * @returns Promise resolving to array of ports
 */
export async function fetchPorts(getToken: () => Promise<string>): Promise<Port[]> {
  const token = await getToken();
  const res = await fetch(`${BASE_URL}${ENDPOINTS.ports}`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!res.ok) throw new Error(`Failed to fetch ports: ${res.status}`);
  const data = await res.json();
  return data.ports || data.results || data.result || (Array.isArray(data) ? data : []);
}

/**
 * Build GeoJSON FeatureCollection from ports array
 */
function buildGeoJson(ports: Port[]): GeoJSON.FeatureCollection {
  const valid = ports.filter(
    (p) =>
      typeof p.longitude === 'number' &&
      typeof p.latitude === 'number' &&
      !Number.isNaN(p.longitude) &&
      !Number.isNaN(p.latitude),
  );
  return {
    type: 'FeatureCollection',
    features: valid.map((port) => ({
      type: 'Feature' as const,
      geometry: {
        type: 'Point' as const,
        coordinates: [port.longitude, port.latitude],
      },
      properties: port as GeoJSON.GeoJsonProperties,
    })),
  };
}

/**
 * Add Ports layer from pre-fetched data
 * @param map - Mapbox map instance
 * @param ports - Array of port objects
 */
export async function addPortsLayer(map: mapboxgl.Map | null, ports: Port[]): Promise<void> {
  if (!map) return;

  // Wait for style to be loaded
  const styleReady = await waitForStyleLoaded(map);
  if (!styleReady) {
    console.warn('Ports: style not ready, skipping');
    return;
  }

  const geojson = buildGeoJson(ports);

  // If no valid ports, clear layer
  if (geojson.features.length === 0) {
    removeGeoJsonLayer(map, LAYER_ID, SOURCE_ID);
    // eslint-disable-next-line no-console
    // No valid port data; layer cleared.
    return;
  }

  // Try to load icon via ImagePreloader registry
  let useIcon = false;
  try {
    const loadedId = await imagePreloader.ensureIconLoaded(map, 'port:default');
    useIcon = !!loadedId;
  } catch {
    useIcon = false;
  }

  // Re-check style after async icon loading
  if (!map.isStyleLoaded()) {
    const ready = await waitForStyleLoaded(map);
    if (!ready) {
      console.warn('Ports: style not ready after icon load, skipping');
      return;
    }
  }

  try {
    // Remove existing layer/source if present
    if (map.getLayer(LAYER_ID)) {
      map.removeLayer(LAYER_ID);
    }
    if (map.getSource(SOURCE_ID)) {
      map.removeSource(SOURCE_ID);
    }

    // Add source
    map.addSource(SOURCE_ID, {
      type: 'geojson',
      data: geojson,
    });

    // Add layer with icon or circle fallback
    if (useIcon) {
      map.addLayer({
        id: LAYER_ID,
        type: 'symbol',
        source: SOURCE_ID,
        layout: {
          'icon-image': ICON_ID,
          'icon-size': 0.25,
          // Allow more ports to be visible at once to match legacy density.
          // Collision detection in Mapbox can hide many symbols when false.
          'icon-allow-overlap': true,
          'icon-ignore-placement': true,
          visibility: 'visible',
        },
      });
      // eslint-disable-next-line no-console
      // Rendering icon-based ports layer features.
    } else {
      map.addLayer({
        id: LAYER_ID,
        type: 'circle',
        source: SOURCE_ID,
        paint: {
          'circle-radius': 4,
          'circle-color': '#3b82f6',
          'circle-stroke-width': 0.5,
          'circle-stroke-color': '#ffffff',
        },
        layout: {
          visibility: 'visible',
        },
      });
      // eslint-disable-next-line no-console
      // Rendering circle-based ports layer features.
    }
  } catch (err) {
    console.error('Ports: error adding layer', err);
  }
}

/**
 * Legacy function for backward compatibility
 * Fetches ports and adds them to the map
 * @deprecated Use fetchPorts + addPortsLayer instead
 */
export function addPorts(map: mapboxgl.Map | null, getToken: () => Promise<string>): void {
  fetchPorts(getToken)
    .then((ports) => {
      addPortsLayer(map, ports);
    })
    .catch((error) => {
      console.error('Failed to load ports:', error);
    });
}
